import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { head } from "@omni/utils/array.utils";
import { cn } from "@omni/utils/styles";

import { CreateAddressModal } from "@/components/Address/modals/CreateOrEditAddressModal/components/CreateAddressModal/createAddressModal.component";
import { EditAddressModal } from "@/components/Address/modals/CreateOrEditAddressModal/components/EditAddressModal/editAddressModal.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import {
    create as createAddress,
    update as updateAddress
} from "@/redux/modules/api/data/address/address.module";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getCreateAddressObject } from "@/utils/address/address";

type CreateOrEditAddressModalProps = {
    address: any;
    type: AddressType | null;
    skipValidation: boolean;
    open: boolean;
    className?: string;
    setOpen: (open: boolean) => void;
    onChange: (addressId: string) => void;
};

const FORM_ID = "addressModal";

const CreateOrEditAddressModal = ({
    address,
    type,
    skipValidation,
    open,
    className,
    setOpen,
    onChange
}: CreateOrEditAddressModalProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const isEditing = !!address?.id;

    const onCreateAddress = async (
        addressing: string | null | undefined,
        firstName: string,
        lastName: string,
        country: string,
        city: string,
        street: string,
        box: string | null | undefined,
        number: string,
        zipCode: string,
        validationStatus: AddressValidationStatus
    ) => {
        const action = await dispatch(
            createAddress([
                getCreateAddressObject(
                    addressing,
                    firstName,
                    lastName,
                    country,
                    city,
                    street,
                    box,
                    number,
                    zipCode,
                    validationStatus
                )
            ])
        );
        const addressId = head(action.payload.addresses)?.id;

        if (addressId) {
            onChange(addressId);
        }
    };

    const onUpdateAddress = async (address: CloudCustomerAddress) => {
        if (address.id) {
            await dispatch(
                updateAddress(
                    address.id,
                    address.addressing,
                    address.firstName,
                    address.lastName,
                    address.country,
                    address.city,
                    address.street,
                    address.box,
                    address.number,
                    address.zipCode,
                    address.validationStatus,
                    false,
                    false
                )
            );
            onChange(address.id);
        }
    };

    if (isEditing) {
        return (
            <EditAddressModal
                title={
                    type
                        ? t(
                              `general.address.modal.title.${type?.toLowerCase()}`
                          )
                        : ""
                }
                subTitle={
                    type
                        ? t(
                              `general.address.modal.subtitle.${type?.toLowerCase()}`
                          )
                        : ""
                }
                formId={FORM_ID}
                setOpen={setOpen}
                open={open}
                className={cn(className)}
                currentAddress={address}
                updateAddress={onUpdateAddress}
                skipValidation={skipValidation}
                handleClosing={false}
            />
        );
    } else {
        return (
            <CreateAddressModal
                address={address}
                createAddress={onCreateAddress}
                isOpen={open}
                className={cn(className)}
                toggleHandler={setOpen}
            />
        );
    }
};

export { CreateOrEditAddressModal };
